import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, FileText, Trash2, Plus, AlertCircle, Loader2 } from "lucide-react";

/**
 * F24Modal — pre-compilazione F24 con anteprima editabile e download PDF/TXT telematico.
 * Non filiamo/paghiamo alcunché: generiamo solo il modulo che l'utente può stampare
 * o caricare sul proprio home banking.
 *
 * Props:
 *   - open: bool
 *   - onOpenChange: (bool) => void
 *   - deadline: (opzionale) scadenza da cui pre-compilare
 */

const SEZIONI = {
  erario: { label: "Erario", color: "bg-secondary/20 text-secondary" },
  inps: { label: "INPS", color: "bg-primary/20 text-primary" },
  regioni: { label: "Regioni", color: "bg-amber-100 text-amber-700" },
  imu: { label: "IMU / Locali", color: "bg-emerald-100 text-emerald-700" },
  altri: { label: "Altri", color: "bg-muted text-muted-foreground" },
};

// Elenco codici tributo comuni per il dropdown "Aggiungi tributo"
const COMMON_CODICI = [
  { codice: "4001", desc: "IRPEF - Saldo", sezione: "erario" },
  { codice: "4033", desc: "IRPEF - Primo acconto", sezione: "erario" },
  { codice: "4034", desc: "IRPEF - Secondo acconto", sezione: "erario" },
  { codice: "1790", desc: "Forfettario - Saldo imposta sostitutiva", sezione: "erario" },
  { codice: "1791", desc: "Forfettario - Primo acconto", sezione: "erario" },
  { codice: "1792", desc: "Forfettario - Secondo acconto", sezione: "erario" },
  { codice: "6001", desc: "IVA mensile gennaio", sezione: "erario" },
  { codice: "6006", desc: "IVA mensile giugno", sezione: "erario" },
  { codice: "6012", desc: "IVA mensile dicembre", sezione: "erario" },
  { codice: "6034", desc: "IVA - Saldo annuale (marzo)", sezione: "erario" },
  { codice: "6099", desc: "Acconto IVA dicembre", sezione: "erario" },
  { codice: "APR", desc: "INPS Artigiani/Commercianti rata fissa", sezione: "inps" },
  { codice: "AP", desc: "INPS Artigiani reddito eccedente", sezione: "inps" },
  { codice: "P10", desc: "INPS Gestione Separata professionisti", sezione: "inps" },
  { codice: "3912", desc: "IMU abitazione principale (A/1, A/8, A/9)", sezione: "imu" },
  { codice: "3918", desc: "IMU altri fabbricati", sezione: "imu" },
  { codice: "3914", desc: "IMU terreni", sezione: "imu" },
  { codice: "3850", desc: "Diritto camerale annuale CCIAA", sezione: "altri" },
];

export default function F24Modal({ open, onOpenChange, deadline = null }) {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null); // 'pdf' | 'txt' | null
  const [context, setContext] = useState(null);
  const [selectedCodice, setSelectedCodice] = useState("");

  // Carica il context quando si apre la modale
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const body = deadline?.id ? { deadline_id: deadline.id } : {};
    api
      .post("/f24/prepare", body)
      .then((r) => setContext(r.data))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Errore preparazione F24");
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
  }, [open, deadline?.id]);

  const updateContribuente = (key, val) => {
    setContext((c) => ({ ...c, contribuente: { ...c.contribuente, [key]: val } }));
  };

  const updateTributoImporto = (idx, field, val) => {
    setContext((c) => {
      const rows = [...c.tributi];
      rows[idx] = { ...rows[idx], [field]: Number(val || 0) };
      const totDeb = rows.reduce((s, r) => s + (r.importo_debito || 0), 0);
      const totCred = rows.reduce((s, r) => s + (r.importo_credito || 0), 0);
      return { ...c, tributi: rows, totale_debito: totDeb, totale_credito: totCred, saldo_finale: totDeb - totCred };
    });
  };

  const updateTributoRateazione = (idx, val) => {
    setContext((c) => {
      const rows = [...c.tributi];
      rows[idx] = { ...rows[idx], rateazione: val };
      return { ...c, tributi: rows };
    });
  };

  const removeTributo = (idx) => {
    setContext((c) => {
      const rows = c.tributi.filter((_, i) => i !== idx);
      const totDeb = rows.reduce((s, r) => s + (r.importo_debito || 0), 0);
      const totCred = rows.reduce((s, r) => s + (r.importo_credito || 0), 0);
      return { ...c, tributi: rows, totale_debito: totDeb, totale_credito: totCred, saldo_finale: totDeb - totCred };
    });
  };

  const addTributo = () => {
    if (!selectedCodice) return;
    const codiceInfo = COMMON_CODICI.find((c) => c.codice === selectedCodice);
    if (!codiceInfo) return;
    setContext((c) => ({
      ...c,
      tributi: [
        ...(c.tributi || []),
        {
          codice: codiceInfo.codice,
          descrizione: codiceInfo.desc,
          sezione: codiceInfo.sezione,
          rateazione: "",
          anno_riferimento: new Date().getFullYear() - 1,
          importo_debito: 0,
          importo_credito: 0,
        },
      ],
    }));
    setSelectedCodice("");
  };

  const download = async (format) => {
    if (!context?.contribuente?.codice_fiscale) {
      toast.error("Codice fiscale obbligatorio. Compilalo nelle Impostazioni.");
      return;
    }
    if (!context.tributi?.length) {
      toast.error("Aggiungi almeno un codice tributo");
      return;
    }
    if (context.saldo_finale <= 0) {
      toast.error("Il saldo deve essere positivo. Verifica gli importi.");
      return;
    }
    setDownloading(format);
    try {
      const r = await api.post("/f24/download", { context, format }, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const ext = format === "txt" ? "txt" : "pdf";
      a.download = `F24_${context.contribuente.codice_fiscale}_${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(format === "pdf" ? "F24 PDF scaricato" : "F24 telematico scaricato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore download");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="f24-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <FileText className="w-6 h-6 text-primary" />
            Prepara F24
          </DialogTitle>
          <DialogDescription>
            Genera un modello F24 pre-compilato. Scarica in PDF (stampabile) o TXT telematico (upload home banking).
          </DialogDescription>
        </DialogHeader>

        {loading || !context ? (
          <div className="py-12 flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin" />
            <div className="text-sm">Preparazione in corso…</div>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Alert CF */}
            {!context.contribuente?.codice_fiscale && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-secondary/10 border border-secondary/30 text-sm">
                <AlertCircle className="w-4 h-4 text-secondary flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold">Codice fiscale mancante</div>
                  <div className="text-xs text-muted-foreground">
                    Vai su <a href="/app/impostazioni" className="underline">Impostazioni</a> e compila anagrafica + CF prima di scaricare.
                  </div>
                </div>
              </div>
            )}

            {/* Anagrafica compatta */}
            <div className="rounded-xl border p-4 bg-accent/20">
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Contribuente</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Codice fiscale *</Label>
                  <Input
                    value={context.contribuente?.codice_fiscale || ""}
                    onChange={(e) => updateContribuente("codice_fiscale", e.target.value.toUpperCase())}
                    className="h-9 mt-1 uppercase"
                    data-testid="f24-cf-input"
                    placeholder="RSSMRA80A15H501U"
                  />
                </div>
                <div>
                  <Label className="text-xs">Cognome e nome</Label>
                  <Input
                    value={context.contribuente?.cognome_nome || ""}
                    onChange={(e) => updateContribuente("cognome_nome", e.target.value)}
                    className="h-9 mt-1"
                    data-testid="f24-nom-input"
                  />
                </div>
              </div>
            </div>

            {/* Tributi */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Tributi</div>
              </div>
              <div className="space-y-2" data-testid="f24-tributi-list">
                {(context.tributi || []).map((t, idx) => {
                  const sez = SEZIONI[t.sezione] || SEZIONI.altri;
                  return (
                    <div key={idx} className="rounded-xl border p-3 flex flex-wrap items-center gap-3" data-testid={`f24-tributo-${idx}`}>
                      <div className="flex-1 min-w-[220px]">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono font-bold text-sm">{t.codice}</span>
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${sez.color}`}>{sez.label}</span>
                        </div>
                        <div className="text-xs text-muted-foreground line-clamp-1">{t.descrizione}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div>
                          <Label className="text-[10px]">Anno rif.</Label>
                          <Input
                            value={t.anno_riferimento || ""}
                            onChange={(e) => setContext((c) => {
                              const rows = [...c.tributi];
                              rows[idx] = { ...rows[idx], anno_riferimento: Number(e.target.value || 0) };
                              return { ...c, tributi: rows };
                            })}
                            className="h-8 w-20 text-sm"
                            data-testid={`f24-anno-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px]">Rateaz.</Label>
                          <Input
                            value={t.rateazione || ""}
                            onChange={(e) => updateTributoRateazione(idx, e.target.value)}
                            className="h-8 w-20 text-sm"
                            placeholder="0101"
                            data-testid={`f24-rateaz-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px]">Debito €</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={t.importo_debito || 0}
                            onChange={(e) => updateTributoImporto(idx, "importo_debito", e.target.value)}
                            className="h-8 w-28 text-sm font-mono"
                            data-testid={`f24-debito-${idx}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px]">Credito €</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={t.importo_credito || 0}
                            onChange={(e) => updateTributoImporto(idx, "importo_credito", e.target.value)}
                            className="h-8 w-28 text-sm font-mono"
                            data-testid={`f24-credito-${idx}`}
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeTributo(idx)}
                          className="h-8 w-8 self-end"
                          data-testid={`f24-remove-${idx}`}
                        >
                          <Trash2 className="w-4 h-4 text-muted-foreground" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Aggiungi tributo */}
              <div className="mt-3 flex items-center gap-2">
                <Select value={selectedCodice} onValueChange={setSelectedCodice}>
                  <SelectTrigger className="h-10 flex-1" data-testid="f24-add-select">
                    <SelectValue placeholder="Aggiungi codice tributo…" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMON_CODICI.map((c) => (
                      <SelectItem key={c.codice} value={c.codice}>
                        <span className="font-mono font-bold mr-2">{c.codice}</span>
                        <span className="text-xs">{c.desc}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={addTributo} disabled={!selectedCodice} className="rounded-full h-10" data-testid="f24-add-btn">
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi
                </Button>
              </div>
            </div>

            {/* Totali */}
            <div className="rounded-xl bg-primary text-primary-foreground p-4 grid grid-cols-3 gap-4" data-testid="f24-totali">
              <div>
                <div className="text-[10px] uppercase opacity-70">Tot. debito</div>
                <div className="font-heading font-bold text-lg font-mono">€ {(context.totale_debito || 0).toFixed(2)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase opacity-70">Tot. credito</div>
                <div className="font-heading font-bold text-lg font-mono">€ {(context.totale_credito || 0).toFixed(2)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase opacity-70">Saldo finale</div>
                <div className="font-heading font-black text-2xl font-mono" data-testid="f24-saldo">€ {(context.saldo_finale || 0).toFixed(2)}</div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button
                onClick={() => download("pdf")}
                disabled={downloading !== null}
                className="rounded-full h-11 flex-1"
                data-testid="f24-download-pdf"
              >
                {downloading === "pdf" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Scarica PDF stampabile
              </Button>
              <Button
                onClick={() => download("txt")}
                disabled={downloading !== null}
                variant="outline"
                className="rounded-full h-11 flex-1"
                data-testid="f24-download-txt"
              >
                {downloading === "txt" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Scarica TXT per home banking
              </Button>
            </div>
            <div className="text-[11px] text-muted-foreground text-center leading-relaxed">
              Il PDF è per stampa/consultazione. Il TXT è nel formato telematico Agenzia delle Entrate: caricalo direttamente nel tuo home banking (Intesa, Unicredit, Fineco, Poste, ecc.).
              <br />TUOTAX non invia e non paga: è un ausilio, verifica sempre col commercialista.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
